import React from "react";
import colors from "../../../../theme/colors";
import { RulesTableCell, IDropDownValue } from "./RulesTableCell";
import { TablesTypeEnum, tablesTypes } from "types/table";
import RulesTablePropertyHeader from "./RulesTablePropertyHeader";
import { ContextMenuTrigger } from "react-contextmenu";

const dataCellStyles = {
  minHeight: "40px",
  minWidth: "200px",
  fontSize: "14px",
  backgroundColor: colors.white,
};

const headerCellStyles = {
  backgroundColor: colors.tables.columnsAndRowsTitles,
  color: colors.primary,
  minWidth: "180px",
  minHeight: "40px",
  padding: "12px 20px",
  fontWeight: "700",
};

const propertiesCellStyles = {
  ...dataCellStyles,
  backgroundColor: colors.tables.hoveredCell,
};

const isLookupItemCheck = (propertyIndex, tableLookupIndex, tableType) => {
  if ([TablesTypeEnum.ATTR_LOOKUP, TablesTypeEnum.LOOKUP].includes(tableType))
    return propertyIndex <= tableLookupIndex;
  return false;
};

export const findRulesetIntersectionInProperties = (ruleTableProperties) => {
  if (!ruleTableProperties) return null;
  const rulesetsByProperty = ruleTableProperties.map(
    (property) => property.ruleSets
  );

  const uniqueRulesets = rulesetsByProperty.flat().reduce((acc, curr) => {
    if (!acc.find((item) => item.id === curr.id)) return [...acc, curr];
    return acc;
  }, []);

  return uniqueRulesets.reduce((acc, curr) => {
    if (
      rulesetsByProperty.every((set) =>
        set.map((ruleset) => ruleset.id).includes(curr.id)
      )
    )
      return [...acc, curr];
    return acc;
  }, []);
};

export type TableMetaDataOption = {
  value: string;
  name: string;
};

/** Options + current values for the TABLE / RULESETS header dropdowns. */
export const getTableMetaData = (
  ruleSets?: Array<{ id: string }>,
  ruleTableData?: any,
  ruleSetsList?: Array<{ id: string; name: string }>
) => {
  const currentRulesets: TableMetaDataOption[] = [];
  if (ruleSets && ruleSets.length > 0) {
    ruleSets.forEach((ruleset) => {
      if (ruleSetsList && ruleSetsList.length > 0) {
        ruleSetsList.forEach((listEl) => {
          if (listEl.id === ruleset?.id) {
            currentRulesets.push({ value: listEl.id, name: listEl.name });
          }
        });
      }
    });
  }

  const availableRulesetFromProperties = findRulesetIntersectionInProperties(
    ruleTableData?.properties
  );

  const formattedRulesets: TableMetaDataOption[] = [];

  if (availableRulesetFromProperties && availableRulesetFromProperties.length) {
    availableRulesetFromProperties.forEach((ruleset) =>
      formattedRulesets.push({ value: ruleset.id, name: ruleset.name })
    );
  }

  if (
    ruleSetsList &&
    ruleSetsList.length > 0 &&
    !availableRulesetFromProperties?.length
  ) {
    ruleSetsList.forEach((ruleset) =>
      formattedRulesets.push({ value: ruleset.id, name: ruleset.name })
    );
  }

  const formattedTablesTypesOptions: TableMetaDataOption[] = tablesTypes.map(
    (type) => ({ value: type, name: type })
  );

  return {
    tableTypeOptions: formattedTablesTypesOptions,
    currentTableType: {
      value: ruleTableData?.tableType,
      name: ruleTableData?.tableType,
    } as TableMetaDataOption,
    rulesetOptions: formattedRulesets,
    currentRulesets,
  };
};

export const getPropertiesData = (
  ruleTableData: any,
  visibleRowsCount?: number,
  readOnly = false
) => {
  let index = 0;
  const selectedProperties = (ruleTableData?.properties ?? []).map(
    (property) => {
      index += 1;
      return {
        content: (
          <RulesTablePropertyHeader
            propertyName={property.name}
            propertyId={property.id}
            ruleTableId={ruleTableData.id}
            index={index}
            readOnly={readOnly}
          />
        ),
        style: headerCellStyles,
        id: property.id,
        isDraggable: !readOnly,
      };
    }
  );

  const rows = ruleTableData?.rows || [];
  const visibleRows =
    typeof visibleRowsCount === "number"
      ? rows.slice(0, visibleRowsCount)
      : rows;

  const ruleTableRows =
    visibleRows.length > 0
      ? visibleRows.map((row) => {
          return row.cells
            .filter((cell) => cell.propertyId)
            .map((cell, index) => {
              const property = ruleTableData?.properties.find(
                (property) => property.id === cell.propertyId
              );

              const { lookUpFieldIndex, tableType } = ruleTableData;

              const isLookupItem = isLookupItemCheck(
                index + 1,
                lookUpFieldIndex,
                tableType
              );

              //Build the info we need to populate drop drop option value
              const cellOptions: Array<IDropDownValue> = property
                ?.propertyValues?.length
                ? property.propertyValues.map((pv) => {
                    return {
                      value: pv.id,
                      label: pv?.valueAttributeCells[0].value, //TODO: assumes Value is first value attribute cell.  Refactor?
                      description: "", //TODO add description pva here
                      isInTableRulesets: pv.isInTableRulesets,
                    };
                  })
                : null;

              //Build drop down option info object for each value in the cell
              const cellValues: Array<IDropDownValue> = (cell.value ?? []).map(
                (v) => {
                  //If we have cell options (discrete property, try to find value and use its info)
                  let valueOption = cellOptions?.find(
                    (option) => option.value === v
                  );

                  //If we have no cell option available, just use our value to populate the object
                  if (!valueOption)
                    valueOption = {
                      value: v,
                      label: v,
                      description: "",
                      isInTableRulesets: false,
                    };
                  return valueOption;
                }
              );

              const { dataType, domain } = property ?? {};

              return {
                content: readOnly ? (
                  <span>
                    {cellValues
                      .map((value) => value.label)
                      .filter(Boolean)
                      .join("; ")}
                  </span>
                ) : (
                  <ContextMenuTrigger id={row.id + cell.propertyId}>
                    <RulesTableCell
                      domain={domain}
                      dataType={dataType}
                      options={cellOptions?.filter((o) => o.isInTableRulesets)}
                      originalValue={cellValues}
                      rowId={row.id}
                      cellId={cell.propertyId}
                      ruleTableId={ruleTableData.id}
                      isLookupItem={isLookupItem}
                    />
                  </ContextMenuTrigger>
                ),
                style: propertiesCellStyles,
                id: "PropertiesRLCell" + cell.propertyId,
                rowId: row.id,
              };
            });
        })
      : [];

  return {
    header: selectedProperties.filter((property) => property?.id),
    rows: ruleTableRows,
  };
};
